import { ComponentProps } from 'react';
import { FormattedRelativeTime } from 'react-intl';

type Unit = ComponentProps<typeof FormattedRelativeTime>['unit'];

const MINUTE_SECONDS = 60;
const HOUR_SECONDS = MINUTE_SECONDS * 60;
const DAY_SECONDS = HOUR_SECONDS * 24;
const WEEK_SECONDS = DAY_SECONDS * 7;
const MONTH_SECONDS = DAY_SECONDS * 30;
const YEAR_SECONDS = DAY_SECONDS * 365;

/**
 * Builds `FormattedRelativeTime` props for `date`, relative to now.
 *
 * The result depends on *when* it is called, so any consumer that is
 * server-rendered produces a hydration mismatch whenever the SSR pass and
 * hydration fall on different sides of a unit boundary — guaranteed once the
 * HTML is cached. Set `suppressHydrationWarning` on the element holding the
 * text to keep that expected drift out of New Relic (US #987145); it silences
 * the warning only, React still re-renders to the client-correct value.
 */
export const getRelativeTimeProps = (
  date: Date | string
): { unit: Unit; value: number } => {
  const now = new Date().getTime();
  const timestamp = new Date(date).getTime();
  const diffSeconds = (timestamp - now) / 1000;
  const absDiffSeconds = Math.abs(diffSeconds);
  const multiplier = diffSeconds < 0 ? -1 : 1;

  if (absDiffSeconds < MINUTE_SECONDS) {
    return { unit: 'second', value: Math.floor(absDiffSeconds) * multiplier };
  }
  if (absDiffSeconds < HOUR_SECONDS) {
    return {
      unit: 'minute',
      value: Math.floor(absDiffSeconds / MINUTE_SECONDS) * multiplier,
    };
  }
  if (absDiffSeconds < DAY_SECONDS) {
    return {
      unit: 'hour',
      value: Math.floor(absDiffSeconds / HOUR_SECONDS) * multiplier,
    };
  }
  if (absDiffSeconds < WEEK_SECONDS) {
    return {
      unit: 'day',
      value: Math.floor(absDiffSeconds / DAY_SECONDS) * multiplier,
    };
  }
  if (absDiffSeconds < MONTH_SECONDS) {
    return {
      unit: 'week',
      value: Math.floor(absDiffSeconds / WEEK_SECONDS) * multiplier,
    };
  }
  if (absDiffSeconds < YEAR_SECONDS) {
    return {
      unit: 'month',
      value: Math.floor(absDiffSeconds / MONTH_SECONDS) * multiplier,
    };
  }
  return {
    unit: 'year',
    value: Math.floor(absDiffSeconds / YEAR_SECONDS) * multiplier,
  };
};
